import { fetchJsonRobust } from './fetch'
import { overpassRateLimiter } from './rateLimiter'
import type { OSMData } from '../../types'
import { titleLooksUninteresting } from '../../utils/text'

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
]

let overpassIdx = 0
let lastRequestTime = 0

/**
 * Check if OSM data looks uninteresting
 */
function osmLooksUninteresting(osm: OSMData): boolean {
  const tags = osm?.tags || {}
  const badPairs = [
    ['highway', 'services'],
    ['highway', 'rest_area'],
    ['amenity', 'parking'],
    ['amenity', 'fuel'],
    ['amenity', 'fast_food'],
    ['amenity', 'restaurant'],
    ['amenity', 'pharmacy'],
    ['amenity', 'dentist'],
    ['leisure', 'pitch'],
    ['leisure', 'sports_centre'],
    ['sport', ''],
  ]

  for (const [k, v] of badPairs) {
    if (tags[k] && (v === '' || tags[k] === v)) return true
  }

  return titleLooksUninteresting(osm?.name || '')
}

/**
 * Fetch OSM data via Overpass API
 */
export async function fetchOSMOverpass(
  lat: number,
  lon: number,
  radius: number
): Promise<OSMData[]> {
  // Rate limiting check - stricter limits
  const rateLimitKey = 'overpass'
  if (!overpassRateLimiter.isAllowed(rateLimitKey)) {
    // Silently skip if rate limited - don't throw error
    return []
  }

  // Minimum interval between requests - increased to 10 seconds
  const now = Date.now()
  const timeSinceLastRequest = now - lastRequestTime
  if (timeSinceLastRequest < 10000) {
    // Skip if too soon - return empty array instead of waiting
    return []
  }
  lastRequestTime = Date.now()
  
  // Skip OSM entirely if we've had recent timeouts to prevent spam
  // This will be reset on next page load
  if (typeof window !== 'undefined') {
    const lastTimeout = sessionStorage.getItem('overpass_last_timeout')
    if (lastTimeout && now - Number(lastTimeout) < 120000) {
      // Skip for 2 minutes after a timeout (increased from 1 minute)
      return []
    }
  }

  // Use a more efficient query with shorter timeout to prevent 504 errors
  // Reduced timeout to 3 seconds to prevent gateway timeouts
  const q = `
[out:json][timeout:3];
(
  node(around:${radius},${lat},${lon})["tourism"];
  node(around:${radius},${lat},${lon})["historic"];
  node(around:${radius},${lat},${lon})["natural"];
  way(around:${radius},${lat},${lon})["tourism"];
  way(around:${radius},${lat},${lon})["historic"];
  way(around:${radius},${lat},${lon})["natural"];
);
out center tags 15;`.trim() // Further reduced to 15 tags and removed some less important categories

  // Try each endpoint with better error handling
  let lastError: Error | null = null
  for (let i = 0; i < OVERPASS_ENDPOINTS.length; i++) {
    const endpoint = OVERPASS_ENDPOINTS[(overpassIdx + i) % OVERPASS_ENDPOINTS.length]
    
    // Skip endpoints that recently timed out or failed
    const timeoutKey = `overpass-timeout-${endpoint}`
    const failedKey = `overpass-failed-${endpoint}`
    const lastTimeout = sessionStorage.getItem(timeoutKey)
    const lastFailed = sessionStorage.getItem(failedKey)
    const now = Date.now()
    
    if (lastTimeout && now - parseInt(lastTimeout, 10) < 120000) {
      continue // Skip this endpoint for 2 minutes after timeout
    }
    if (lastFailed && now - parseInt(lastFailed, 10) < 300000) {
      continue // Skip this endpoint for 5 minutes after failure
    }
    
    try {
      const data = await fetchJsonRobust(
        endpoint,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: q,
        },
        { timeoutMs: 4000, retries: 0 } // Reduced timeout to 4s to prevent 504 errors
      )
      
      // Success - rotate to next endpoint for next time
      overpassIdx = (overpassIdx + 1) % OVERPASS_ENDPOINTS.length
      
      const els = data?.elements || []
      const out: OSMData[] = []

      for (const e of els) {
        const tags = e.tags || {}
        const name = tags.name
        if (!name) continue

        const elLat = e.lat ?? (e.center ? e.center.lat : null)
        const elLon = e.lon ?? (e.center ? e.center.lon : null)
        if (typeof elLat !== 'number' || typeof elLon !== 'number') continue

        const obj: OSMData = {
          id: e.id,
          osmType: e.type,
          lat: elLat,
          lon: elLon,
          name,
          tags,
          wikidata: tags.wikidata || null,
          wikipedia: tags.wikipedia || null,
        }

        if (osmLooksUninteresting(obj)) continue
        out.push(obj)
      }

      return out
    } catch (e) {
      // If it's a timeout (504) or network error, mark it and try next endpoint
      if (e instanceof Error && (e.message.includes('504') || e.message.includes('timeout') || 
          e.message.includes('Gateway') || e.message.includes('aborted') || 
          e.message.includes('503') || e.message.includes('408'))) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(timeoutKey, String(Date.now()))
          sessionStorage.setItem('overpass_last_timeout', String(Date.now()))
          // Mark this endpoint as failed for longer (5 minutes)
          sessionStorage.setItem(`overpass-failed-${endpoint}`, String(Date.now()))
        }
        lastError = e
        // Continue to next endpoint instead of breaking
        continue
      }
      lastError = e instanceof Error ? e : new Error(String(e))
      // Continue to next endpoint for other errors
      continue
    }
  }

  // If all endpoints failed, return empty array instead of throwing
  if (lastError) {
    console.warn('All Overpass endpoints failed, returning empty result:', lastError)
    return []
  }
  return []
}
